import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Globe, Building2, Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { getLocations } from '../services/api';

const CONTINENTS = [
  'All',
  'North America',
  'South America',
  'Europe',
  'Asia',
  'Africa',
  'Oceania'
];

const LocationSearch = ({ onFilterChange, filters }) => {
  const [locations, setLocations] = useState({ countries: [], cities: [], continents: [] });
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContinentChange = (value) => {
    onFilterChange({ ...filters, continent: value, country: '', city: '' });
  };

  const handleCountryChange = (value) => {
    onFilterChange({ ...filters, country: value === 'all' ? '' : value, city: '' });
  };

  const handleCityChange = (value) => {
    onFilterChange({ ...filters, city: value === 'all' ? '' : value });
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    onFilterChange({ ...filters, search: e.target.value });
  };

  const clearFilters = () => {
    setSearchText('');
    onFilterChange({ continent: 'All', country: '', city: '', search: '' });
  };

  const hasActiveFilters = filters.continent !== 'All' || filters.country || filters.city || filters.search;

  // Filter countries based on selected continent
  const filteredCountries = filters.continent && filters.continent !== 'All'
    ? locations.countries
    : locations.countries;

  return (
    <div ref={containerRef} className="bg-white border-b border-gray-100">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {/* Collapsed View - Search Bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, city, country..."
              value={searchText}
              onChange={handleSearchChange}
              onFocus={() => setIsExpanded(true)}
              className="pl-10 pr-10"
            />
            {searchText && (
              <button
                onClick={() => { setSearchText(''); onFilterChange({ ...filters, search: '' }); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <Button
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
            className={`flex items-center gap-2 ${hasActiveFilters ? 'border-emerald-500 text-emerald-600' : ''}`}
          >
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Location Filter</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-emerald-500 rounded-full" />
            )}
          </Button>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Expanded View - Location Filters */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 animate-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Continent */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4" />
                  Continent
                </label>
                <Select value={filters.continent || 'All'} onValueChange={handleContinentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Continents" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTINENTS.map((continent) => (
                      <SelectItem key={continent} value={continent}>
                        {continent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Country */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4" />
                  Country
                </label>
                <Select value={filters.country || 'all'} onValueChange={handleCountryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {filteredCountries.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4" />
                  City
                </label>
                <Select value={filters.city || 'all'} onValueChange={handleCityChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {locations.cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active filters display */}
            {hasActiveFilters && (
              <div className="mt-4 flex flex-wrap gap-2">
                {filters.continent && filters.continent !== 'All' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                    <Globe className="w-3 h-3" />
                    {filters.continent}
                    <button onClick={() => onFilterChange({ ...filters, continent: 'All' })} className="ml-1 hover:text-emerald-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.country && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    <MapPin className="w-3 h-3" />
                    {filters.country}
                    <button onClick={() => onFilterChange({ ...filters, country: '' })} className="ml-1 hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.city && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    <Building2 className="w-3 h-3" />
                    {filters.city}
                    <button onClick={() => onFilterChange({ ...filters, city: '' })} className="ml-1 hover:text-purple-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSearch;
